import { Component } from '@theme/component';

/**
 * Announcement row. Columns stay in one line and can be dragged
 * when they overflow the bar.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #scrollLeft = 0;

  /** @type {boolean} */
  #dragging = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(() => this.#updateOverflow());
    this.#resizeObserver.observe(this);
    this.#updateOverflow();

    this.addEventListener('pointerdown', this.#onPointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  #updateOverflow() {
    const overflows = this.scrollWidth > this.clientWidth + 1;
    this.classList.toggle('is-scrollable', overflows);

    if (overflows) {
      this.setAttribute('tabindex', '0');
    } else {
      this.removeAttribute('tabindex');
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.classList.contains('is-scrollable')) return;
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    this.#dragging = false;
    this.#startX = event.clientX;
    this.#scrollLeft = this.scrollLeft;
    this.setPointerCapture(event.pointerId);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const delta = event.clientX - this.#startX;
    if (!this.#dragging && Math.abs(delta) < 4) return;

    this.#dragging = true;
    this.classList.add('is-dragging');
    this.scrollLeft = this.#scrollLeft - delta;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.classList.remove('is-dragging');

    if (this.#dragging) {
      this.addEventListener(
        'click',
        (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        },
        { capture: true, once: true }
      );
    }

    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
